import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, Table, Typography, Tag, Space, Button, notification, Spin, Empty, Modal, Descriptions, Divider, Select } from 'antd';
import { ReloadOutlined, EyeOutlined, CalendarOutlined, FileTextOutlined, LoadingOutlined } from '@ant-design/icons';
import CustomNavbar from '../components/Navbar';
import PaymentService from '../services/paymentService';

const NonSearchableSelect = (props) => <Select {...props} showSearch={false} />;

const { Title, Text } = Typography;

const PaymentHistory = () => {
    const [loading, setLoading] = useState(true);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    const fetchPaymentHistory = useCallback(async (currentPage, pageSize) => {
        setLoading(true);
        try {
            const response = await PaymentService.getPaymentHistory(
                currentPage,
                pageSize
            );

            if (response.success) {
                console.log('Payment history data:', response.data.data);
                setPaymentHistory(response.data.data);
                setTotal(response.data.total);
            } else {
                notification.error({
                    message: 'Failed to fetch payment history',
                    description: response.message || 'Something went wrong.',
                });
            }
        } catch (error) {
            console.error('Error fetching payment history:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to load payment history. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPaymentHistory(currentPage, pageSize);
    }, [fetchPaymentHistory, currentPage, pageSize]);

    const handleTableChange = (paginationInfo) => {
        setCurrentPage(paginationInfo.current);
        setPageSize(paginationInfo.pageSize);
    };

    const formatCurrency = (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'null' || dateString === 'undefined') {
            return 'N/A';
        }

        const date = new Date(dateString);

        // Check if the date is valid (not NaN and not epoch date)
        if (isNaN(date.getTime()) || date.getTime() === 0) {
            return 'N/A';
        }

        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'success':
                return 'success';
            case 'failed':
                return 'error';
            case 'pending':
                return 'processing';
            case 'cancelled':
                return 'default';
            default:
                return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status.toLowerCase()) {
            case 'success':
                return 'Success';
            case 'failed':
                return 'Failed';
            case 'pending':
                return 'Pending';
            case 'cancelled':
                return 'Cancelled';
            default:
                return status;
        }
    };

    const columns = [
        {
            title: 'Order ID',
            dataIndex: 'order_id',
            key: 'order_id',
            render: (orderId) => (
                <Text code style={{ fontSize: '12px' }}>
                    {orderId}
                </Text>
            ),
        },
        {
            title: 'Plan',
            dataIndex: ['subscription_plan', 'name'],
            key: 'plan',
            render: (planName) => (
                <Text strong>{planName}</Text>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => (
                <Text strong style={{ color: '#52c41a' }}>
                    {formatCurrency(amount, record.currency)}
                </Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Payment Method',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: (method) => (
                <Text>{method || 'N/A'}</Text>
            ),
        },
        {
            title: 'Transaction ID',
            dataIndex: 'transaction_id',
            key: 'transaction_id',
            render: (txnId) => (
                <Text code style={{ fontSize: '11px' }}>
                    {txnId || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'paid_at',
            key: 'paid_at',
            render: (_, record) => {
                // Try paid_at first, then created_at as fallback
                const dateToShow = record.paid_at || record.created_at;
                return <Text>{formatDate(dateToShow)}</Text>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record)}
                    >
                        View
                    </Button>
                </Space>
            ),
        },
    ];

    const handleViewDetails = (record) => {
        console.log('Payment record for modal:', record);
        console.log('Subscription plan data:', record.subscription_plan);
        setSelectedPayment(record);
        setIsViewModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsViewModalVisible(false);
        setSelectedPayment(null);
    };

    const handleRefresh = () => {
        fetchPaymentHistory(currentPage, pageSize);
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <Helmet><title>Payment History | Oottan</title></Helmet>
            <CustomNavbar />

            <div style={{ padding: '80px 24px 24px' }}>
                <Card style={{ borderRadius: '12px' }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <div>
                            <Title level={3} style={{ margin: 0 }}>
                                Payment History
                            </Title>
                            <Text type="secondary">
                                View all your payment transactions
                            </Text>
                        </div>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefresh}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </div>

                    {/* Payment History Table */}
                    {loading ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '400px'
                        }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                        </div>
                    ) : paymentHistory.length === 0 ? (
                        <Empty
                            description="No payment history found"
                            style={{ margin: '40px 0' }}
                        />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={paymentHistory}
                            rowKey="id"
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: total,
                                showSizeChanger: true,
                                showQuickJumper: false,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} payments`,
                                pageSizeOptions: ['10', '15', '25', '50'],
                                selectComponentClass: NonSearchableSelect,
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 1200 }}
                        />
                    )}

                    {/* Summary */}
                    {paymentHistory.length > 0 && (
                        <div style={{
                            marginTop: '24px',
                            padding: '16px',
                            background: '#f6f6f6',
                            borderRadius: '8px'
                        }}>
                            <Text strong>
                                Total Payments: {total} |
                                Showing {((currentPage - 1) * pageSize) + 1} to{' '}
                                {Math.min(currentPage * pageSize, total)} of{' '}
                                {total} payments
                            </Text>
                        </div>
                    )}
                </Card>
            </div>

            {/* Payment Details Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileTextOutlined style={{ color: '#AD1457' }} />
                        <span>Payment Details</span>
                    </div>
                }
                open={isViewModalVisible}
                onCancel={handleCloseModal}
                footer={[
                    <Button key="close" onClick={handleCloseModal}>
                        Close
                    </Button>
                ]}
                width={500}
                centered
                style={{ top: 50 }}
                bodyStyle={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 0' }}
            >
                {selectedPayment && (
                    <div>
                        {/* Payment Status Header */}
                        <div style={{
                            background: getStatusColor(selectedPayment.status) === 'success' ? '#f6ffed' :
                                getStatusColor(selectedPayment.status) === 'error' ? '#fff2f0' : '#e6f7ff',
                            border: `1px solid ${getStatusColor(selectedPayment.status) === 'success' ? '#b7eb8f' :
                                getStatusColor(selectedPayment.status) === 'error' ? '#ffccc7' : '#91d5ff'}`,
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '24px',
                            textAlign: 'center'
                        }}>
                            <Tag color={getStatusColor(selectedPayment.status)} size="large" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                {getStatusText(selectedPayment.status)}
                            </Tag>
                            <div style={{ marginTop: '8px', fontSize: '24px', fontWeight: '700', color: '#52c41a' }}>
                                {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                            </div>
                        </div>

                        {/* Payment Details */}
                        <Descriptions title="Payment Information" bordered column={1} size="small">
                            <Descriptions.Item label="Order ID">
                                <Text code>{selectedPayment.order_id}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Plan">
                                <Text strong>{selectedPayment.subscription_plan?.name || 'N/A'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Amount">
                                <Text strong style={{ color: '#52c41a' }}>
                                    {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Payment Date">
                                <Text>
                                    <CalendarOutlined style={{ marginRight: '4px' }} />
                                    {formatDate(selectedPayment.paid_at)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Created Date">
                                <Text>
                                    <CalendarOutlined style={{ marginRight: '4px' }} />
                                    {formatDate(selectedPayment.created_at)}
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>

                        {/* Plan Details */}
                        {selectedPayment.subscription_plan && (
                            <>
                                <Divider />
                                <div>
                                    <Title level={4} style={{ marginBottom: '16px' }}>Plan Details</Title>

                                    {/* Basic Plan Info */}
                                    <Descriptions bordered column={1} size="small" style={{ marginBottom: '16px' }}>
                                        <Descriptions.Item label="Plan Name">
                                            <Text strong>{selectedPayment.subscription_plan.name}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Duration">
                                            <Text>{selectedPayment.subscription_plan.duration || (selectedPayment.subscription_plan.duration_days ? `${selectedPayment.subscription_plan.duration_days} days` : 'N/A')}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Contact Numbers">
                                            <Text>{selectedPayment.subscription_plan.contact_numbers || selectedPayment.subscription_plan.contactNumbers || 'N/A'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Price">
                                            <Text strong style={{ color: '#52c41a' }}>
                                                {formatCurrency(selectedPayment.subscription_plan.price, selectedPayment.subscription_plan.currency || 'INR')}
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Description">
                                            <Text>{selectedPayment.subscription_plan.description || 'N/A'}</Text>
                                        </Descriptions.Item>
                                    </Descriptions>




                                </div>
                            </>
                        )}


                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PaymentHistory;
